import { useState } from 'react'
import { useToast } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import { Route } from '../../config/router'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { Country, getIdentityVerificationRoute, isConsumerRequiredToVerifyIdentity, isCountryNeedsSoftIdAttempt } from '../../model'
import {
  useAppRouter,
  useAppDispatch,
  getIdentities,
  fetchAccount,
  fetchCreditLimit,
  setLoggedInLoadingMessageKey,
  setSkipBiometricCheck,
  useLazyGetAccountQuery,
  useLazyGetIdentitiesQuery,
  useLazyGetCreditLimitQuery,
} from '../../state'
import { TrackingEvent } from '../../model/amplitude'
import { ConsumerAccountIdentitiesResponse } from '@afterpaytouch/portal-api/consumer/account/types'
import { LaunchDarklyFlags, useFlag } from '../useFlag'
import { isPortalApiError } from '@afterpaytouch/portal-api'
import { extractApiError } from '../extractApiError'
import { useRegulatoryFlow } from '../useRegulatoryFlow/useRegulatoryFlow'

export interface UseLoggedInRedirectReturn {
  isLoading: boolean
  error: Error | null
  loggedInRedirect: () => Promise<void>
}

export const useLoggedInRedirect = (): UseLoggedInRedirectReturn => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const setToast = useToast()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const softIdRetryEnabled = useFlag(LaunchDarklyFlags.SoftIdRetryEnabled)
  const isPortalSignupEnabled = useFlag(LaunchDarklyFlags.EnabledPortalSignup)
  const completeUnsuitabilityOnHomepage = useFlag(LaunchDarklyFlags.CompleteAuUnsuitabilityOnHomepage)
  const { handleRegulatoryFlow } = useRegulatoryFlow()
  const useRtkQuery = useFlag(LaunchDarklyFlags.PortalRtkQueryMigration)
  const [triggerGetAccount] = useLazyGetAccountQuery()
  const [triggerGetIdentities] = useLazyGetIdentitiesQuery()
  const [triggerGetCreditLimit] = useLazyGetCreditLimitQuery()

  const loggedInRedirect = async (): Promise<void> => {
    try {
      setIsLoading(true)
      setError(null)

      const accountData = useRtkQuery ? await triggerGetAccount().unwrap() : await dispatch(fetchAccount()).unwrap()

      const isProfileConfirmed = accountData.profileConfirmed

      // Profile confirm flow
      if (!isProfileConfirmed) {
        if (isPortalSignupEnabled) {
          router.replace(Route.SIGNUP)
        } else {
          router.replace(Route.PROFILE_CONFIRM)
        }
        return
      }

      const identityCheckStatus = accountData.identityCheckStatus
      const needSoftIdAttempt = isCountryNeedsSoftIdAttempt(accountData.countryCode)
      const requiredToVerifyIdentity = isConsumerRequiredToVerifyIdentity(identityCheckStatus)

      let identitiesResult: ConsumerAccountIdentitiesResponse | undefined
      if (needSoftIdAttempt && requiredToVerifyIdentity) {
        identitiesResult = useRtkQuery ? await triggerGetIdentities().unwrap() : await dispatch(getIdentities()).unwrap()
        logEvent(TrackingEvent.GET_IDENTITY_ON_LOGGED_IN_PAGE, {
          identitiesResult,
        })
      }

      // IDV flow
      if (requiredToVerifyIdentity) {
        const identityVerificationRoute = getIdentityVerificationRoute(softIdRetryEnabled, needSoftIdAttempt, identitiesResult, accountData.countryCode)
        if (identityVerificationRoute === Route.VERIFYING_BIOMETRIC) {
          dispatch(setSkipBiometricCheck(true))
        }
        logEvent(TrackingEvent.REDIRECT_ON_LOGGED_IN_PAGE, {
          route: identityVerificationRoute,
        })
        router.replace(identityVerificationRoute)
        return
      }

      // Regulatory flow (AU + GB)
      const isAuRegFlow = !completeUnsuitabilityOnHomepage && accountData.countryCode === Country.AU
      const isGbConsumer = accountData.countryCode === Country.GB
      if (isAuRegFlow || isGbConsumer) {
        try {
          if (useRtkQuery) {
            await triggerGetCreditLimit().unwrap()
          } else {
            await dispatch(fetchCreditLimit()).unwrap()
          }
          logEvent(TrackingEvent.FETCH_CREDIT_LIMIT_ON_LOGGED_IN_PAGE)
        } catch (creditLimitError: unknown) {
          const apiError = extractApiError(useRtkQuery, creditLimitError)
          if (handleRegulatoryFlow(apiError)) return
        }
      }
      logEvent(TrackingEvent.REDIRECT_ON_LOGGED_IN_PAGE, {
        route: Route.HOME,
      })
      dispatch(setLoggedInLoadingMessageKey(null))
      router.replace(Route.HOME)
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error(String(err)))
      setToast({
        message: t('common:error.defaultErrorMsg'),
        kind: 'Failure',
      })

      // Safe access of properties for tracking
      const portalError = extractApiError(useRtkQuery, err)
      const errorData = isPortalApiError(portalError) ? { errorCode: portalError.errorCode } : {}
      logEvent(TrackingEvent.VIEWED_ERROR_TOAST_ON_LOGGED_IN_PAGE, { err, ...errorData })
      router.replace(Route.INDEX)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    error,
    loggedInRedirect,
  }
}
